import type { ChatType } from '@form-factory/schema';
import { useLingui } from '@lingui/react/macro';
import { Button } from '@utima/ui';
import { Copy, CopyCheckIcon } from 'lucide-react';
import { memo, useCallback, useState } from 'react';

import { SimpleTooltip } from '@/components';
import { Card } from '@/components/card/Card';

export interface ChatbotScriptFieldsProps {
  data?: ChatType;
}

export const ChatbotScriptFields = memo(function ChatbotScriptFields({
  data,
}: ChatbotScriptFieldsProps) {
  const { t } = useLingui();
  const [isCopied, setIsCopied] = useState(false);

  const script = `<script>
  var script = document.createElement('script');
  script.src = 'https://loader.form-factory.utima.dev/v0/chatbot.js';
  script.type = 'module';
  script.async = true;
  script.addEventListener('load', function () {
    var chatbotElement = document.createElement('form-factory-chatbot');
    chatbotElement.setAttribute('chat', '${data?.id}');
    document.body.append(chatbotElement);
  });

  document.body.append(script);
</script>`;

  const copyToClipboard = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      navigator.clipboard.writeText(script).then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      });
    },
    [script],
  );

  if (!data) {
    return null;
  }

  return (
    <Card
      title={
        <span className='flex items-center gap-2'>
          <span>{t`Script`}</span>
          <SimpleTooltip title={t`Copy`} delayDuration={0}>
            <Button
              data-cta='admin-chatbot-script-copy-button'
              onClick={copyToClipboard}
              variant='primary'
              size='icon-xs'
            >
              {isCopied ? (
                <CopyCheckIcon className='size-4' />
              ) : (
                <Copy className='size-4' />
              )}
            </Button>
          </SimpleTooltip>
        </span>
      }
    >
      <div className='relative'>
        <pre className='mb-4 whitespace-pre-wrap font-mono text-xs'>
          {script}
        </pre>
      </div>
    </Card>
  );
});
